<template>
  <div class="gl-page">
    <!-- Header -->
    <div class="gl-page-header">
      <div>
        <h1 class="gl-page-title">Subscription Plans</h1>
        <div class="gl-breadcrumb">
          <router-link to="/" class="gl-breadcrumb-link">Home</router-link>
          <span class="gl-breadcrumb-sep">/</span>
          <span class="gl-breadcrumb-link">Super Admin</span>
          <span class="gl-breadcrumb-sep">/</span>
          <span>Plans</span>
        </div>
      </div>
      <button class="gl-btn-add" @click="openCreateModal">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        Add Plan
      </button>
    </div>

    <!-- Skeleton -->
    <div v-if="loading" class="gl-plans-grid">
      <div v-for="i in 4" :key="i" class="gl-plan-card gl-plan-skel-card">
        <div class="gl-plan-card-top">
          <div class="gl-skel" style="width:44px;height:44px;border-radius:12px"></div>
          <div class="gl-skel" style="width:54px;height:20px;border-radius:20px"></div>
        </div>
        <div class="gl-skel" style="height:18px;width:60%;border-radius:8px;margin-bottom:6px"></div>
        <div class="gl-skel" style="height:13px;width:40%;border-radius:6px;margin-bottom:20px"></div>
        <div class="gl-plan-feats-skel">
          <div v-for="j in 3" :key="j" class="gl-skel" style="height:12px;border-radius:6px"></div>
        </div>
        <div class="gl-plan-card-foot">
          <div class="gl-skel" style="height:13px;width:80px;border-radius:6px"></div>
          <div style="display:flex;gap:8px">
            <div class="gl-skel" style="width:32px;height:32px;border-radius:8px"></div>
            <div class="gl-skel" style="width:32px;height:32px;border-radius:8px"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty -->
    <div v-else-if="plans.length === 0" class="gl-card">
      <div class="gl-empty-msg">
        <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="opacity:.3;margin-bottom:10px"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
        <div>No plans yet</div>
        <button class="gl-btn-add" style="margin-top:12px" @click="openCreateModal">Create your first plan</button>
      </div>
    </div>

    <!-- Plan cards grid -->
    <div v-else class="gl-plans-grid">
      <div v-for="plan in plans" :key="plan.id" class="gl-plan-card" :class="{ 'gl-plan-inactive': !plan.is_active }">
        <div class="gl-plan-card-top">
          <div class="gl-plan-icon" :class="'gl-plan-icon-' + getPlanColor(plan.slug)">
            <svg v-if="plan.slug === 'free'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><path d="M12 22V7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>
            <svg v-else-if="plan.slug === 'starter'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
            <svg v-else-if="plan.slug === 'professional'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/></svg>
            <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-4 0v2"/></svg>
          </div>
          <span :class="plan.is_active ? 'gl-badge gl-badge-active' : 'gl-badge gl-badge-muted'" style="font-size:11px">
            {{ plan.is_active ? 'Active' : 'Inactive' }}
          </span>
        </div>

        <div class="gl-plan-name">{{ plan.name }}</div>
        <div class="gl-plan-slug">{{ plan.slug }}</div>

        <div class="gl-plan-price">
          <span v-if="plan.price > 0">
            <span class="gl-plan-price-rm">RM</span>
            <span class="gl-plan-price-val">{{ formatCurrency(plan.price) }}</span>
            <span class="gl-plan-price-cycle">/{{ plan.billing_cycle || 'mo' }}</span>
          </span>
          <span v-else class="gl-plan-price-free">Free</span>
        </div>

        <div v-if="plan.description" class="gl-plan-desc">{{ plan.description }}</div>

        <div class="gl-plan-feats">
          <div v-if="plan.max_outlets" class="gl-plan-feat">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            {{ plan.max_outlets }} Outlets
          </div>
          <div v-if="plan.max_products" class="gl-plan-feat">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            {{ plan.max_products }} Products
          </div>
          <div v-if="plan.max_staff" class="gl-plan-feat">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            {{ plan.max_staff }} Staff
          </div>
          <div v-if="plan.trial_days" class="gl-plan-feat">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            {{ plan.trial_days }}-day trial
          </div>
        </div>

        <div class="gl-plan-card-foot">
          <div class="gl-plan-subs">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            {{ plan.subscribers_count || 0 }} subscribers
          </div>
          <div class="gl-plan-actions">
            <button class="gl-act-btn gl-act-edit" @click="openEditModal(plan)" title="Edit">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            </button>
            <button class="gl-act-btn" :class="plan.is_active ? 'gl-act-warn' : 'gl-act-success'" @click="togglePlan(plan)" :title="plan.is_active ? 'Deactivate' : 'Activate'">
              <svg v-if="plan.is_active" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
              <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal -->
    <div v-if="showModal" class="gl-modal-overlay" @click.self="showModal = false">
      <div class="gl-modal">
        <div class="gl-modal-header">
          <h2 class="gl-modal-title">{{ editingPlan ? 'Edit Plan' : 'Create Plan' }}</h2>
          <button class="gl-modal-close" @click="showModal = false">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div class="gl-modal-body">
          <div class="gl-form-row">
            <div class="gl-form-group">
              <label class="gl-label">Plan Name <span class="gl-required">*</span></label>
              <input type="text" v-model="form.name" class="gl-input" placeholder="e.g. Starter, Professional" />
            </div>
            <div class="gl-form-group">
              <label class="gl-label">Slug <span class="gl-required">*</span></label>
              <input type="text" v-model="form.slug" class="gl-input" placeholder="e.g. starter, professional" />
            </div>
          </div>

          <div class="gl-form-row">
            <div class="gl-form-group">
              <label class="gl-label">Price (RM)</label>
              <input type="number" v-model="form.price" class="gl-input" min="0" step="0.01" placeholder="0.00" />
            </div>
            <div class="gl-form-group">
              <label class="gl-label">Billing Cycle</label>
              <select v-model="form.billing_cycle" class="gl-input gl-select-input">
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>
          </div>

          <div class="gl-form-group">
            <label class="gl-label">Description</label>
            <textarea v-model="form.description" class="gl-input gl-textarea" rows="2" placeholder="Brief description of the plan"></textarea>
          </div>

          <div class="gl-form-divider">
            <span>Limits &amp; Features</span>
          </div>

          <div class="gl-form-row gl-form-row-3">
            <div class="gl-form-group">
              <label class="gl-label">Max Outlets</label>
              <input type="number" v-model="form.max_outlets" class="gl-input" min="1" placeholder="e.g. 1" />
            </div>
            <div class="gl-form-group">
              <label class="gl-label">Max Products</label>
              <input type="number" v-model="form.max_products" class="gl-input" min="1" placeholder="e.g. 50" />
            </div>
            <div class="gl-form-group">
              <label class="gl-label">Max Staff</label>
              <input type="number" v-model="form.max_staff" class="gl-input" min="1" placeholder="e.g. 5" />
            </div>
          </div>

          <div class="gl-form-row">
            <div class="gl-form-group">
              <label class="gl-label">Trial Days</label>
              <input type="number" v-model="form.trial_days" class="gl-input" min="0" placeholder="e.g. 14" />
            </div>
            <div class="gl-form-group">
              <label class="gl-label">Sort Order</label>
              <input type="number" v-model="form.sort_order" class="gl-input" min="0" placeholder="e.g. 1" />
            </div>
          </div>

          <label class="gl-toggle-label">
            <div class="gl-toggle" :class="{ 'gl-toggle-on': form.is_active }" @click="form.is_active = !form.is_active">
              <div class="gl-toggle-thumb"></div>
            </div>
            <span>Active — visible to customers</span>
          </label>
        </div>

        <div class="gl-modal-footer">
          <button class="gl-btn-cancel" @click="showModal = false">Cancel</button>
          <button class="gl-btn-save" @click="savePlan" :disabled="saving">
            {{ saving ? 'Saving…' : (editingPlan ? 'Update Plan' : 'Create Plan') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { superAdminPlanApi } from '@/services/superAdminApi';

export default {
  name: 'PlanManager',
  data() {
    return {
      loading: false,
      saving: false,
      plans: [],
      showModal: false,
      editingPlan: null,
      form: { name: '', slug: '', price: '', billing_cycle: 'monthly', description: '', max_outlets: '', max_products: '', max_staff: '', trial_days: '', sort_order: '', is_active: true }
    };
  },
  created() { this.fetchPlans(); },
  methods: {
    async fetchPlans() {
      this.loading = true;
      try {
        const response = await superAdminPlanApi.list();
        if (response.data.success) this.plans = response.data.data;
      } catch (error) { console.error('Failed to fetch plans:', error); }
      finally { this.loading = false; }
    },
    openCreateModal() {
      this.editingPlan = null;
      this.form = { name: '', slug: '', price: '', billing_cycle: 'monthly', description: '', max_outlets: '', max_products: '', max_staff: '', trial_days: '', sort_order: '', is_active: true };
      this.showModal = true;
    },
    openEditModal(plan) {
      this.editingPlan = plan;
      this.form = { name: plan.name, slug: plan.slug, price: plan.price, billing_cycle: plan.billing_cycle || 'monthly', description: plan.description || '', max_outlets: plan.max_outlets || '', max_products: plan.max_products || '', max_staff: plan.max_staff || '', trial_days: plan.trial_days || '', sort_order: plan.sort_order || '', is_active: plan.is_active };
      this.showModal = true;
    },
    async savePlan() {
      if (!this.form.name || !this.form.slug) { alert('Please fill in required fields'); return; }
      this.saving = true;
      try {
        const payload = { ...this.form, price: parseFloat(this.form.price) || 0, max_outlets: this.form.max_outlets ? parseInt(this.form.max_outlets) : null, max_products: this.form.max_products ? parseInt(this.form.max_products) : null, max_staff: this.form.max_staff ? parseInt(this.form.max_staff) : null, trial_days: this.form.trial_days ? parseInt(this.form.trial_days) : 0, sort_order: this.form.sort_order ? parseInt(this.form.sort_order) : 0 };
        const response = this.editingPlan ? await superAdminPlanApi.update(this.editingPlan.id, payload) : await superAdminPlanApi.create(payload);
        if (response.data.success) { this.showModal = false; this.fetchPlans(); }
      } catch (error) { alert(error.response?.data?.message || 'Failed to save plan'); }
      finally { this.saving = false; }
    },
    async togglePlan(plan) {
      try {
        const response = await superAdminPlanApi.update(plan.id, { is_active: !plan.is_active });
        if (response.data.success) plan.is_active = !plan.is_active;
      } catch (error) { console.error('Failed to toggle plan:', error); }
    },
    formatCurrency(value) {
      if (!value) return '0.00';
      return parseFloat(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },
    getPlanColor(slug) {
      return { free: 'muted', starter: 'blue', professional: 'teal', enterprise: 'amber' }[slug] || 'teal';
    }
  }
};
</script>

<style scoped>
.gl-page { padding: 24px 28px 48px; }
.gl-page-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 24px; flex-wrap: wrap; }
.gl-page-title { font-size: 22px; font-weight: 800; color: #1a2e2b; letter-spacing: -0.4px; margin: 0 0 4px; }
.gl-breadcrumb { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #8a9e9a; }
.gl-breadcrumb-link { color: #900C27; text-decoration: none; }
.gl-breadcrumb-link:hover { text-decoration: underline; }
.gl-breadcrumb-sep { opacity: .5; }

.gl-btn-add { display: flex; align-items: center; gap: 7px; padding: 9px 18px; background: #900C27; color: #fff; border: none; border-radius: 10px; font-size: 13px; font-weight: 700; cursor: pointer; white-space: nowrap; transition: background .15s; }
.gl-btn-add:hover { background: #1a5248; }

/* ── Plan cards ── */
.gl-plans-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
.gl-plan-card { background: #fff; border: 1px solid #eaedec; border-radius: 16px; padding: 20px; display: flex; flex-direction: column; gap: 0; transition: box-shadow .18s; }
.gl-plan-card:hover { box-shadow: 0 4px 20px rgba(0,0,0,.07); }
.gl-plan-inactive { opacity: .65; }
.gl-plan-card-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.gl-plan-icon { width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center; }
.gl-plan-icon-teal  { background: rgba(144,12,39,.12); color: #900C27; }
.gl-plan-icon-blue  { background: rgba(59,130,246,.12); color: #3b82f6; }
.gl-plan-icon-amber { background: rgba(245,158,11,.12); color: #d97706; }
.gl-plan-icon-muted { background: #f0f3f2; color: #8a9e9a; }
.gl-plan-name { font-size: 17px; font-weight: 800; color: #1a2e2b; margin-bottom: 3px; }
.gl-plan-slug { font-size: 11.5px; color: #8a9e9a; margin-bottom: 14px; }
.gl-plan-price { margin-bottom: 16px; }
.gl-plan-price-rm { font-size: 13px; font-weight: 600; color: #2a3d3a; vertical-align: super; }
.gl-plan-price-val { font-size: 26px; font-weight: 800; color: #1a2e2b; letter-spacing: -1px; }
.gl-plan-price-cycle { font-size: 13px; color: #8a9e9a; margin-left: 2px; }
.gl-plan-price-free { font-size: 22px; font-weight: 800; color: #16a34a; }
.gl-plan-desc { font-size: 12.5px; color: #6b7f7b; margin-bottom: 14px; line-height: 1.5; }
.gl-plan-feats { display: flex; flex-direction: column; gap: 7px; flex: 1; margin-bottom: 18px; }
.gl-plan-feat { display: flex; align-items: center; gap: 7px; font-size: 12.5px; color: #2a3d3a; }
.gl-plan-feat svg { color: #900C27; flex-shrink: 0; }
.gl-plan-card-foot { display: flex; align-items: center; justify-content: space-between; border-top: 1px solid #f0f3f2; padding-top: 14px; margin-top: auto; }
.gl-plan-subs { display: flex; align-items: center; gap: 5px; font-size: 12px; color: #8a9e9a; }
.gl-plan-actions { display: flex; gap: 6px; }
.gl-act-btn { width: 32px; height: 32px; border-radius: 8px; border: 1px solid; display: flex; align-items: center; justify-content: center; cursor: pointer; background: transparent; transition: all .15s; }
.gl-act-edit { border-color: #900C27; color: #900C27; }
.gl-act-edit:hover { background: rgba(144,12,39,.08); }
.gl-act-warn { border-color: #d97706; color: #d97706; }
.gl-act-warn:hover { background: rgba(217,119,6,.08); }
.gl-act-success { border-color: #16a34a; color: #16a34a; }
.gl-act-success:hover { background: rgba(22,163,74,.08); }

/* ── Skeleton plan card ── */
.gl-plan-skel-card { display: flex; flex-direction: column; gap: 10px; }
.gl-plan-feats-skel { display: flex; flex-direction: column; gap: 8px; flex: 1; margin-bottom: 18px; }

/* ── Badges ── */
.gl-badge { display: inline-block; font-size: 11px; font-weight: 600; padding: 3px 9px; border-radius: 20px; white-space: nowrap; }
.gl-badge-active { background: #e0f0ec; color: #1a5248; }
.gl-badge-muted  { background: #f0f3f2; color: #6b7f7b; }

/* ── Empty ── */
.gl-card { background: #fff; border: 1px solid #eaedec; border-radius: 14px; overflow: hidden; }
.gl-empty-msg { padding: 60px 0; text-align: center; font-size: 13px; color: #a0b5b0; display: flex; flex-direction: column; align-items: center; }

/* ── Skeleton ── */
.gl-skel { background: linear-gradient(90deg, #f0f2f1 25%, #e5e8e7 50%, #f0f2f1 75%); background-size: 200% 100%; animation: gl-shimmer 1.4s infinite; border-radius: 6px; }
@keyframes gl-shimmer { to { background-position: -200% 0; } }

/* ── Modal ── */
.gl-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.4); display: flex; align-items: center; justify-content: center; z-index: 1050; padding: 16px; }
.gl-modal { background: #fff; border-radius: 18px; width: 100%; max-width: 560px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 60px rgba(0,0,0,.18); }
.gl-modal-header { display: flex; align-items: center; justify-content: space-between; padding: 22px 24px 16px; border-bottom: 1px solid #f0f3f2; }
.gl-modal-title { font-size: 17px; font-weight: 800; color: #1a2e2b; margin: 0; }
.gl-modal-close { width: 32px; height: 32px; border-radius: 8px; border: 1px solid #eaedec; background: #fff; color: #6b7f7b; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all .15s; }
.gl-modal-close:hover { background: #f5f7f6; color: #1a2e2b; }
.gl-modal-body { padding: 22px 24px; display: flex; flex-direction: column; gap: 16px; }
.gl-modal-footer { padding: 16px 24px 22px; border-top: 1px solid #f0f3f2; display: flex; justify-content: flex-end; gap: 10px; }

/* ── Form ── */
.gl-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.gl-form-row-3 { grid-template-columns: 1fr 1fr 1fr; }
.gl-form-group { display: flex; flex-direction: column; gap: 6px; }
.gl-label { font-size: 12.5px; font-weight: 600; color: #2a3d3a; }
.gl-required { color: #dc2626; }
.gl-input { padding: 9px 12px; border: 1px solid #e0e7e5; border-radius: 9px; font-size: 13px; color: #2a3d3a; background: #fff; outline: none; transition: border-color .15s; width: 100%; box-sizing: border-box; }
.gl-input:focus { border-color: #900C27; }
.gl-select-input { cursor: pointer; }
.gl-textarea { resize: vertical; min-height: 64px; }
.gl-form-divider { display: flex; align-items: center; gap: 10px; font-size: 12px; font-weight: 700; color: #8a9e9a; text-transform: uppercase; letter-spacing: .4px; }
.gl-form-divider::before, .gl-form-divider::after { content: ''; flex: 1; height: 1px; background: #eef0ef; }
.gl-toggle-label { display: flex; align-items: center; gap: 10px; cursor: pointer; font-size: 13px; color: #2a3d3a; }
.gl-toggle { width: 40px; height: 22px; border-radius: 100px; background: #e0e7e5; transition: background .2s; position: relative; flex-shrink: 0; }
.gl-toggle-on { background: #900C27; }
.gl-toggle-thumb { width: 16px; height: 16px; border-radius: 50%; background: #fff; position: absolute; top: 3px; left: 3px; transition: transform .2s; box-shadow: 0 1px 3px rgba(0,0,0,.2); }
.gl-toggle-on .gl-toggle-thumb { transform: translateX(18px); }

.gl-btn-cancel { padding: 9px 18px; border: 1px solid #e0e7e5; border-radius: 9px; background: #fff; color: #6b7f7b; font-size: 13px; font-weight: 600; cursor: pointer; }
.gl-btn-cancel:hover { background: #f5f7f6; }
.gl-btn-save { padding: 9px 22px; background: #900C27; color: #fff; border: none; border-radius: 9px; font-size: 13px; font-weight: 700; cursor: pointer; white-space: nowrap; transition: background .15s; }
.gl-btn-save:hover:not(:disabled) { background: #1a5248; }
.gl-btn-save:disabled { opacity: .6; cursor: not-allowed; }

/* ── Mobile ── */
@media (max-width: 767px) {
  .gl-page { padding: 14px 16px 40px; }
  .gl-page-header { margin-bottom: 16px; }
  .gl-breadcrumb { display: none; }
  .gl-plans-grid { grid-template-columns: 1fr; gap: 12px; }
  .gl-form-row { grid-template-columns: 1fr; }
  .gl-form-row-3 { grid-template-columns: 1fr 1fr; }
  .gl-modal { border-radius: 16px 16px 0 0; position: fixed; bottom: 0; left: 0; right: 0; max-width: 100%; max-height: 90vh; }
  .gl-modal-overlay { align-items: flex-end; padding: 0; }
}

@media (min-width: 768px) and (max-width: 1199px) {
  .gl-plans-grid { grid-template-columns: repeat(2, 1fr); }
}
</style>

<style>
body.dark-only .gl-page-title { color: #e0eded; }
body.dark-only .gl-breadcrumb-link { color: #5a7470; }
body.dark-only .gl-plan-card { background: #1e2d2b; border-color: #2a3d3a; }
body.dark-only .gl-plan-name { color: #e0eded; }
body.dark-only .gl-plan-slug { color: #5a7470; }
body.dark-only .gl-plan-price-val { color: #e0eded; }
body.dark-only .gl-plan-price-rm { color: #c0d4d0; }
body.dark-only .gl-plan-price-cycle { color: #5a7470; }
body.dark-only .gl-plan-feat { color: #c0d4d0; }
body.dark-only .gl-plan-card-foot { border-top-color: #2a3d3a; }
body.dark-only .gl-plan-subs { color: #5a7470; }
body.dark-only .gl-plan-icon-muted { background: #2a3d3a; color: #5a7470; }
body.dark-only .gl-plan-desc { color: #5a7470; }
body.dark-only .gl-skel { background: linear-gradient(90deg, #243432 25%, #2a3d3a 50%, #243432 75%); background-size: 200% 100%; }
body.dark-only .gl-card { background: #1e2d2b; border-color: #2a3d3a; }
body.dark-only .gl-empty-msg { color: #5a7470; }
body.dark-only .gl-modal { background: #1e2d2b; }
body.dark-only .gl-modal-header { border-bottom-color: #2a3d3a; }
body.dark-only .gl-modal-title { color: #e0eded; }
body.dark-only .gl-modal-close { background: #1e2d2b; border-color: #2a3d3a; color: #5a7470; }
body.dark-only .gl-modal-close:hover { background: #243432; color: #e0eded; }
body.dark-only .gl-modal-footer { border-top-color: #2a3d3a; }
body.dark-only .gl-label { color: #c0d4d0; }
body.dark-only .gl-input { background: #16211f; border-color: #2a3d3a; color: #c0d4d0; }
body.dark-only .gl-input:focus { border-color: #3d9e8c; }
body.dark-only .gl-form-divider { color: #5a7470; }
body.dark-only .gl-form-divider::before, body.dark-only .gl-form-divider::after { background: #2a3d3a; }
body.dark-only .gl-toggle-label { color: #c0d4d0; }
body.dark-only .gl-toggle { background: #2a3d3a; }
body.dark-only .gl-btn-cancel { background: #1e2d2b; border-color: #2a3d3a; color: #c0d4d0; }
body.dark-only .gl-btn-cancel:hover { background: #243432; }
</style>
